// Undo for a room that has no undo.
//
// The room rejects, it never transforms, and it keeps no history a client can
// ask it to rewind to. So undo here is ORDINARY EDITING in reverse: every
// accepted op is paired with the value it replaced, and undoing proposes that
// value back as a fresh intent, cited against whatever rev the entity holds
// NOW. That has two consequences worth being explicit about:
//
//   • An undo can be refused like any edit (someone else moved the clip since;
//     the slot filled up). It then does nothing, exactly like the edit would.
//   • A recreated clip or track gets a NEW id — the room never resurrects. The
//     entry therefore records DATA (ClipData + audioKey), never ids, for
//     anything it may have to bring back.
//
// Values are captured at proposal time from the client's own view (the room
// with in-flight edits folded over it), so a burst of moves before the
// broadcasts land still records each step against the previous one. Revs are
// resolved at UNDO time from the canonical replica — the only source that
// knows what the room currently holds.

import type { Intent } from "@shared/sync/intents.js";
import type { InstrumentId } from "@shared/instruments.js";
import { CENTER_PAN, UNITY_VOLUME } from "@shared/mixer.js";
import type { ClipData } from "@shared/sync/protocol.js";
import {
  cloneDevices,
  type AcceptedOp,
  type CanonicalRoom,
  type CanonicalTrackState,
  type ChainOwner,
  type DeviceState,
  type TrackField,
  type TrackFieldValue,
  type TrackKind,
} from "@shared/sync/state.js";
import { clipDataOf } from "@web-shared/lib/edits";

export type UndoStep =
  /** An update landed; put the previous payload back — and the previous
   * object, when the update swapped it (a reverse/normalize/fade). */
  | { kind: "restoreClip"; id: string; trackId: string; before: ClipData; audioKey: string | null }
  /** A create landed; remove it. */
  | { kind: "removeClip"; id: string }
  /** A delete landed; create the clip again (new id). `id` is the one it
   * had: older entries still name it, and are re-pointed at the new one when
   * the create lands (`rebornIds` / `remapEntry`). */
  | { kind: "recreateClip"; id?: string; trackId: string; before: ClipData; audioKey: string | null }
  | { kind: "restoreTrackField"; id: string; field: TrackField; before: TrackFieldValue }
  | { kind: "restoreTrackOrder"; id: string; before: number }
  | { kind: "removeTrack"; id: string }
  /** A track (and, by cascade, its clips) was deleted; build it again. */
  | {
      kind: "recreateTrack";
      id?: string;
      name: string;
      ord: number;
      trackKind?: TrackKind;
      instrument?: InstrumentId;
      clips: Array<{ id?: string; before: ClipData; audioKey: string | null }>;
    }
  | { kind: "restoreTempo"; before: number }
  | { kind: "restoreSceneNames"; before: Array<string | null> }
  | { kind: "restoreCuePoints"; before: Array<{ name: string; time: number }> }
  | { kind: "restoreMaster"; before: { volume: number; pan: number } }
  | { kind: "restoreTrackSends"; id: string; before: number[] }
  | { kind: "restoreTrackInstrument"; id: string; before: InstrumentId }
  | { kind: "restoreDeviceParam"; id: string; device: number; param: number; before: number }
  | { kind: "restoreTrackDevices"; id: string; before: DeviceState[] }
  | { kind: "restoreChainParam"; owner: ChainOwner; device: number; param: number; before: number }
  | { kind: "restoreChain"; owner: ChainOwner; before: DeviceState[] }
  | { kind: "restoreReturns"; before: Array<{ name: string; mute: boolean; solo: boolean; volume: number; pan: number }> };

export interface UndoEntry {
  /** Human label for the toolbar ("move 2 clips"). */
  label: string;
  /** In the order they should be PROPOSED — already the reverse of the ops. */
  steps: UndoStep[];
}

/** Local handle for a create — the room echoes it back on the accepted op. */
function mintTempId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function trackFieldOf(t: CanonicalTrackState, field: TrackField): TrackFieldValue {
  return t[field];
}

/**
 * Pair every accepted op with the value it displaced in `before` — the room
 * as this client saw it when it proposed. Rejected intents changed nothing and
 * leave no step. Returns null when nothing landed.
 */
export function recordUndo(before: CanonicalRoom, accepted: AcceptedOp[], label: string): UndoEntry | null {
  const steps: UndoStep[] = [];
  for (const op of accepted) {
    switch (op.kind) {
      case "clipUpserted": {
        const prior = before.clips.find((c) => c.id === op.id);
        if (op.tempId !== undefined || !prior) {
          steps.push({ kind: "removeClip", id: op.id });
        } else {
          steps.push({
            kind: "restoreClip",
            id: op.id,
            trackId: prior.trackId,
            before: clipDataOf(prior),
            audioKey: prior.audioKey,
          });
        }
        break;
      }
      case "clipDeleted": {
        const prior = before.clips.find((c) => c.id === op.id);
        if (prior) {
          steps.push({
            kind: "recreateClip",
            id: prior.id,
            trackId: prior.trackId,
            before: clipDataOf(prior),
            audioKey: prior.audioKey,
          });
        }
        break;
      }
      case "trackCreated":
        steps.push({ kind: "removeTrack", id: op.id });
        break;
      case "trackFieldSet": {
        const prior = before.tracks.find((t) => t.id === op.id);
        if (prior) {
          steps.push({ kind: "restoreTrackField", id: op.id, field: op.field, before: trackFieldOf(prior, op.field) });
        }
        break;
      }
      case "trackOrderSet": {
        const prior = before.tracks.find((t) => t.id === op.id);
        if (prior) steps.push({ kind: "restoreTrackOrder", id: op.id, before: prior.ord });
        break;
      }
      case "trackDeleted": {
        const prior = before.tracks.find((t) => t.id === op.id);
        if (prior) {
          steps.push({
            kind: "recreateTrack",
            id: prior.id,
            name: prior.name,
            ord: prior.ord,
            ...(prior.trackKind === "midi" ? { trackKind: "midi" as const } : {}),
            ...(prior.instrument ? { instrument: prior.instrument } : {}),
            clips: before.clips
              .filter((c) => c.trackId === op.id)
              .map((c) => ({ id: c.id, before: clipDataOf(c), audioKey: c.audioKey })),
          });
        }
        break;
      }
      case "tempoSet":
        steps.push({ kind: "restoreTempo", before: before.tempo });
        break;
      case "sceneNamesSet":
        steps.push({ kind: "restoreSceneNames", before: [...(before.sceneNames ?? [])] });
        break;
      case "cuePointsSet":
        steps.push({ kind: "restoreCuePoints", before: (before.cuePoints ?? []).map((c) => ({ ...c })) });
        break;
      case "masterSet":
        steps.push({ kind: "restoreMaster", before: { ...(before.master ?? { volume: UNITY_VOLUME, pan: CENTER_PAN }) } });
        break;
      case "trackSendsSet": {
        const prior = before.tracks.find((t) => t.id === op.id);
        // Padded to the op's length: undoing the first send move must
        // assert a zero where nothing was held, or `setTrackSends []` says
        // nothing to Live and the level it wrote stays up (2026-09-08).
        if (prior) {
          const before = [...(prior.sends ?? [])];
          while (before.length < op.sends.length) before.push(0);
          steps.push({ kind: "restoreTrackSends", id: op.id, before });
        }
        break;
      }
      case "trackInstrumentSet": {
        const prior = before.tracks.find((t) => t.id === op.id);
        if (prior) steps.push({ kind: "restoreTrackInstrument", id: op.id, before: prior.instrument ?? "sine" });
        break;
      }
      case "deviceParamSet": {
        const prior = before.tracks.find((t) => t.id === op.id)?.devices?.[op.device]?.params[op.param];
        if (prior) steps.push({ kind: "restoreDeviceParam", id: op.id, device: op.device, param: op.param, before: prior.value });
        break;
      }
      case "trackDevicesSet": {
        const prior = before.tracks.find((t) => t.id === op.id);
        if (prior) steps.push({ kind: "restoreTrackDevices", id: op.id, before: cloneDevices(prior.devices ?? []) });
        break;
      }
      case "chainParamSet": {
        const prior = before.chains?.[op.owner]?.devices[op.device]?.params[op.param];
        if (prior) steps.push({ kind: "restoreChainParam", owner: op.owner, device: op.device, param: op.param, before: prior.value });
        break;
      }
      case "chainSet": {
        const prior = before.chains?.[op.owner];
        if (prior && prior.rev > 0) steps.push({ kind: "restoreChain", owner: op.owner, before: cloneDevices(prior.devices) });
        break;
      }
      case "returnsSet":
        steps.push({ kind: "restoreReturns", before: (before.returns ?? []).map((r) => ({ ...r })) });
        break;
    }
  }
  if (steps.length === 0) return null;
  // Reverse: the last thing that happened is the first thing to put back —
  // which also restores the vacate-before-occupy order of a group move.
  return { label, steps: steps.reverse() };
}

/**
 * The intents that carry out an entry against the room AS IT IS NOW. Steps
 * whose target has since vanished, or that cannot be expressed (an audio clip
 * whose object the room never had), are dropped rather than proposed into a
 * certain rejection.
 */
export function undoIntents(room: CanonicalRoom, entry: UndoEntry): Intent[] {
  return undoPlan(room, entry).intents;
}

/**
 * `undoIntents`, plus which create brings back which old id (`reborn`:
 * tempId → the id the clip or track had). The room never resurrects, so the
 * thing comes back under a new id while every older entry still names the
 * old one — and an entry whose clip is "gone" is dropped, which made the
 * next undo skip to an older action. The caller re-points the history once
 * the creates land (`rebornIds`, `remapEntry`).
 */
export function undoPlan(room: CanonicalRoom, entry: UndoEntry): { intents: Intent[]; reborn: Map<string, string> } {
  const intents: Intent[] = [];
  const reborn = new Map<string, string>();
  const born = (prefix: string, oldId: string | undefined): string => {
    const tempId = mintTempId(prefix);
    if (oldId !== undefined) reborn.set(tempId, oldId);
    return tempId;
  };
  // A reorder's restores travel as one gesture, like the reorder did.
  const orderGesture = entry.steps.filter((s) => s.kind === "restoreTrackOrder").length > 1 ? mintTempId("gesture") : null;
  for (const step of entry.steps) {
    switch (step.kind) {
      case "restoreClip": {
        const clip = room.clips.find((c) => c.id === step.id);
        if (!clip) break;
        intents.push({
          kind: "upsertClip",
          id: clip.id,
          tempId: `undo_${clip.id}`,
          trackId: room.tracks.some((t) => t.id === step.trackId) ? step.trackId : clip.trackId,
          baseRev: clip.rev,
          clip: step.before,
          // Null means "keep the room's object"; name the old key only when
          // the room has since swapped it, so an ordinary move never
          // re-asserts a key it didn't touch.
          audioKey: step.audioKey !== null && step.audioKey !== clip.audioKey ? step.audioKey : null,
        });
        break;
      }
      case "removeClip": {
        const clip = room.clips.find((c) => c.id === step.id);
        if (clip) intents.push({ kind: "deleteClip", id: clip.id, baseRev: clip.rev });
        break;
      }
      case "recreateClip": {
        if (!room.tracks.some((t) => t.id === step.trackId)) break;
        const midi = step.before.clipKind === "midi";
        if (!midi && step.audioKey === null) break; // the room could never serve it
        intents.push({
          kind: "upsertClip",
          id: null,
          tempId: born("undo", step.id),
          trackId: step.trackId,
          baseRev: null,
          clip: step.before,
          audioKey: step.audioKey,
        });
        break;
      }
      case "restoreTrackField": {
        const track = room.tracks.find((t) => t.id === step.id);
        if (!track) break;
        intents.push({ kind: "setTrackField", id: track.id, baseRev: track.rev, field: step.field, value: step.before });
        break;
      }
      case "removeTrack": {
        const track = room.tracks.find((t) => t.id === step.id);
        if (track) intents.push({ kind: "deleteTrack", id: track.id, baseRev: track.rev });
        break;
      }
      case "restoreTrackOrder": {
        const track = room.tracks.find((t) => t.id === step.id);
        if (track) {
          intents.push({
            kind: "setTrackOrder",
            id: track.id,
            baseRev: track.rev,
            ord: step.before,
            ...(orderGesture ? { gestureId: orderGesture } : {}),
          });
        }
        break;
      }
      case "recreateTrack": {
        // One gesture: the lane and its clips land together or not at all, so
        // a refused clip never leaves a half-restored lane behind.
        const gestureId = mintTempId("gesture");
        const tempId = born("track", step.id);
        intents.push({
          kind: "createTrack",
          tempId,
          name: step.name,
          ord: step.ord,
          ...(step.trackKind === "midi" ? { trackKind: "midi" as const } : {}),
          ...(step.trackKind === "midi" && step.instrument ? { instrument: step.instrument } : {}),
          gestureId,
        });
        for (const c of step.clips) {
          const midi = c.before.clipKind === "midi";
          if (!midi && c.audioKey === null) continue;
          intents.push({
            kind: "upsertClip",
            id: null,
            tempId: born("undo", c.id),
            trackId: tempId,
            baseRev: null,
            clip: c.before,
            audioKey: c.audioKey,
            gestureId,
          });
        }
        break;
      }
      case "restoreTempo":
        intents.push({ kind: "setTempo", baseRev: room.tempoRev, value: step.before });
        break;
      case "restoreSceneNames":
        intents.push({ kind: "setSceneNames", baseRev: room.scenesRev ?? 0, names: [...step.before] });
        break;
      case "restoreCuePoints":
        intents.push({ kind: "setCuePoints", baseRev: room.cuePointsRev ?? 0, points: step.before.map((c) => ({ ...c })) });
        break;
      case "restoreMaster":
        intents.push({ kind: "setMaster", baseRev: room.masterRev ?? 0, volume: step.before.volume, pan: step.before.pan });
        break;
      case "restoreTrackSends": {
        const track = room.tracks.find((t) => t.id === step.id);
        if (track) intents.push({ kind: "setTrackSends", id: track.id, baseRev: track.rev, sends: [...step.before] });
        break;
      }
      case "restoreTrackInstrument": {
        const track = room.tracks.find((t) => t.id === step.id);
        if (track) intents.push({ kind: "setTrackInstrument", id: track.id, baseRev: track.rev, instrument: step.before });
        break;
      }
      case "restoreDeviceParam": {
        const track = room.tracks.find((t) => t.id === step.id);
        if (track?.devices?.[step.device]?.params[step.param]) {
          intents.push({ kind: "setDeviceParam", id: track.id, baseRev: track.devicesRev ?? 0, device: step.device, param: step.param, value: step.before });
        }
        break;
      }
      case "restoreTrackDevices": {
        const track = room.tracks.find((t) => t.id === step.id);
        if (track) intents.push({ kind: "setTrackDevices", id: track.id, baseRev: track.devicesRev ?? 0, devices: cloneDevices(step.before) });
        break;
      }
      case "restoreChainParam": {
        const chain = room.chains?.[step.owner];
        if (chain?.devices[step.device]?.params[step.param]) {
          intents.push({ kind: "setChainParam", owner: step.owner, baseRev: chain.rev, device: step.device, param: step.param, value: step.before });
        }
        break;
      }
      case "restoreChain":
        intents.push({ kind: "setChain", owner: step.owner, baseRev: room.chains?.[step.owner]?.rev ?? 0, devices: cloneDevices(step.before) });
        break;
      case "restoreReturns":
        intents.push({ kind: "setReturns", baseRev: room.returnsRev ?? 0, returns: step.before.map((r) => ({ ...r })) });
        break;
    }
  }
  return { intents, reborn };
}

/** Old id → the id the room gave it back under, from what an undo's creates
 * were answered with. */
export function rebornIds(reborn: Map<string, string>, accepted: AcceptedOp[]): Map<string, string> {
  const ids = new Map<string, string>();
  for (const op of accepted) {
    if ((op.kind !== "clipUpserted" && op.kind !== "trackCreated") || op.tempId === undefined) continue;
    const old = reborn.get(op.tempId);
    if (old !== undefined && old !== op.id) ids.set(old, op.id);
  }
  return ids;
}

/** Re-point an entry at clips and tracks that came back under new ids. */
export function remapEntry(entry: UndoEntry, ids: Map<string, string>): UndoEntry {
  if (ids.size === 0) return entry;
  const to = (id: string): string => ids.get(id) ?? id;
  const opt = (id: string | undefined): { id?: string } => (id === undefined ? {} : { id: to(id) });
  const steps = entry.steps.map((step): UndoStep => {
    switch (step.kind) {
      case "restoreClip":
        return { ...step, id: to(step.id), trackId: to(step.trackId) };
      case "recreateClip":
        return { ...step, ...opt(step.id), trackId: to(step.trackId) };
      case "recreateTrack":
        return { ...step, ...opt(step.id), clips: step.clips.map((c) => ({ ...c, ...opt(c.id) })) };
      case "removeClip":
      case "removeTrack":
      case "restoreTrackField":
      case "restoreTrackOrder":
      case "restoreTrackSends":
      case "restoreTrackInstrument":
      case "restoreDeviceParam":
      case "restoreTrackDevices":
        return { ...step, id: to(step.id) };
      default:
        return step;
    }
  });
  return { ...entry, steps };
}

/** A label for the toolbar, from what the intents are about to do. */
export function describeIntents(intents: Intent[]): string {
  const counts = new Map<string, number>();
  for (const i of intents) {
    const what =
      i.kind === "upsertClip"
        ? i.id === null
          ? "add clip"
          : "edit clip"
        : i.kind === "deleteClip"
          ? "delete clip"
          : i.kind === "createTrack"
            ? "add track"
            : i.kind === "deleteTrack"
              ? "delete track"
              : i.kind === "setTrackField"
                ? `set ${i.field}`
                : i.kind === "setTrackOrder"
                  ? "reorder lane"
                  : i.kind === "setSceneNames"
                    ? "name scenes"
                    : i.kind === "setCuePoints"
                      ? "set locators"
                      : i.kind === "setMaster"
                        ? "set master"
                        : i.kind === "setTrackSends"
                          ? "set sends"
                          : i.kind === "setTrackInstrument"
                            ? "change instrument"
                          : i.kind === "setDeviceParam" || i.kind === "setChainParam"
                            ? "turn a knob"
                            : i.kind === "setTrackDevices" || i.kind === "setChain"
                              ? "set devices"
                          : i.kind === "setReturns"
                            ? "set returns"
                            : "set tempo";
    counts.set(what, (counts.get(what) ?? 0) + 1);
  }
  return [...counts]
    .map(([what, n]) => (n === 1 ? what : `${what} ×${n}`))
    .join(", ");
}
